import { Suspense, lazy, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';

import profilesAPI from '@/api/profiles';
import { WorkingProfileForm } from '@/components/EditProfilePage/schema';
import IntegrationsRedirect from '@/components/IntegrationsRedirect';
import LoadingAnimation from '@/components/LoadingAnimation';
import {
  buildClaimProfile,
  claimOnboarding,
  uploadPreviewAssets,
} from '@/components/OnboardingPage/onboardingApi';
import { ContactExchange } from '@/components/Templates/ContactExchange';
import { DigitalWallet } from '@/components/Templates/DigitalWallet';
import EmailSignature from '@/components/Templates/EmailSignature';
import { ProfileDesign } from '@/components/Templates/ProfileDesign';
import {
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  INTERCOM_WORKSPACE_ID,
} from '@/config/env';
import {
  OnboardingSession,
  OnboardingSessionData,
} from '@/helpers/sessionUtils';
import { StateProvider } from '@/hooks/useAppState';
import useAuth, { AuthProvider } from '@/hooks/useAuth';
import { DialogProvider } from '@/hooks/useDialog';
import { ShopifyProvider } from '@/hooks/useShopify';
import AccountRecoveryPage from '@/pages/AccountRecoveryPage';
import ActivationKeysListPage from '@/pages/ActivationKeysListPage';
import AnalyticsAdminPage from '@/pages/AnalyticsAdminPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import AuthLinkAccountPage from '@/pages/AuthLinkAccountPage';
import ContactListAdminPage from '@/pages/ContactListAdminPage';
import ContactListPage from '@/pages/ContactListPage';
import CreateGroupPage from '@/pages/CreateGroupPage';
import EditGroupPage from '@/pages/EditGroupPage';
import EditProfileAdminPage from '@/pages/EditProfileAdminPage';
import EditProfilePage from '@/pages/EditProfilePage';
import EditSharedProfilePage from '@/pages/EditSharedProfilePage';
import EmailSignaturePreviewPage from '@/pages/EmailSignaturePreviewPage';
import GroupListPage from '@/pages/GroupListPage';
import LoginPage from '@/pages/LoginPage';
import NewActivationPage from '@/pages/NewActivationPage';
import NotFoundPage from '@/pages/NotFoundPage';
import OAuth2AuthorizePage from '@/pages/OAuth2AuthorizePage';
import OldAnalyticsPage from '@/pages/OldAnalyticsPage';
import OnboardingPage from '@/pages/OnboardingPage';
import OrdersV2ListAdminPage from '@/pages/OrderV2Page/OrdersV2ListAdmin/Page';
import OrganisationsListPage from '@/pages/OrganisationsListPage';
import PilotsListPage from '@/pages/PilotsListPage';
import PreViewPage from '@/pages/PreviewPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import ProductsListAdminPage from '@/pages/ProductsListAdminPage';
import ProductsListPage from '@/pages/ProductsListPage';
import ProfileGeneratorPage from '@/pages/ProfileGeneratorPage';
import ProfileListAdminPage from '@/pages/ProfileListAdminPage';
import ProfileListGroupPage from '@/pages/ProfileListGroupPage';
import ProfileListPage from '@/pages/ProfileListPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SampleProfilePage from '@/pages/SampleProfilePage';
import AccountSettingsAdminPage from '@/pages/SettingsAdminPage';
import AccountSettingsPage from '@/pages/SettingsPage';
import ShopPage from '@/pages/ShopPage';
import ShopifyLoginPage from '@/pages/ShopifyLoginPage';
import ShopifyVerifyOTPCodePage from '@/pages/ShopifyVerifyOTPCodePage';
import SignUpPage from '@/pages/SignUpPage';
import SsoConsumePage from '@/pages/SsoConsumePage';
import SuperuserAnalyticsPage from '@/pages/SuperuserAnalyticsPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import TestComponents from '@/pages/TestComponents';
import VerifyOTPCodePage from '@/pages/VerifyOTPCodePage';
import ViewOrganisationAdminPage from '@/pages/ViewOrganisationAdminPage';
import ViewOrganisationPage from '@/pages/ViewOrganisationPage';
import ViewProfilePage from '@/pages/ViewProfilePage';
import SubscriptionStatus from '@/types/SubscriptionStatus';

import Layout from './Layout';
import { PseudoTabs, TemplatesPseudoTabs } from './PseudoTabs';

const queryClient = new QueryClient();

const PostOnboardingModal = lazy(() =>
  import('@/components/OnboardingPage/PostOnboardingModal').then(module => ({
    default: module.PostOnboardingModal,
  })),
);

function AuthenticatedRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return <Navigate to="/login" />;
  }

  const notRequiredSubscriptionRoutes = ['/settings', '/shop'];
  if (
    user.platform_subscription_status === SubscriptionStatus.INACTIVE &&
    !notRequiredSubscriptionRoutes.includes(location.pathname)
  ) {
    return <Navigate to="/settings" replace />;
  }

  return <Outlet />;
}

function RootLayout() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.initialize(GOOGLE_ANALYTICS_MEASUREMENT_ID || 'G-12345');
  }, []);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: `${location.pathname}${location.search}`,
    });
  }, [location]);

  return (
    <IntercomProvider
      appId={INTERCOM_WORKSPACE_ID}
      autoBootProps={{
        hideDefaultLauncher: true,
      }}
      autoBoot
    >
      <AuthProvider>
        <StateProvider>
          <QueryClientProvider client={queryClient}>
            <ShopifyProvider>
              <DialogProvider>
                <Outlet />
                <Toaster position="top-right" richColors />
              </DialogProvider>
            </ShopifyProvider>
          </QueryClientProvider>
        </StateProvider>
      </AuthProvider>
    </IntercomProvider>
  );
}

export default function Router() {
  return <RouterProvider router={router} />;
}

const router = createBrowserRouter([
  {
    id: 'root',
    element: <RootLayout />,
    children: [
      // public routes
      { path: '/onboarding', element: <OnboardingPage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/sign-up', element: <SignUpPage /> },
      { path: '/verify-mfa', element: <VerifyOTPCodePage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/account-recovery', element: <AccountRecoveryPage /> },
      { path: '/sso', element: <SsoConsumePage /> },
      {
        path: '/oauth/zapier',
        element: <OAuth2AuthorizePage clientName="Zapier" />,
      },
      { path: '/oauth2/authorize', element: <OAuth2AuthorizePage /> },
      { path: '/auth/callback', element: <AuthCallbackPage /> },
      { path: '/auth/link-account', element: <AuthLinkAccountPage /> },
      { path: '/view-card/:card_hash_id', element: <NewActivationPage /> },
      { path: '/shopify/verify-mfa', element: <ShopifyVerifyOTPCodePage /> },
      { path: '/shopify/login', element: <ShopifyLoginPage /> },
      { path: '/preview/:order_uuid', element: <PreViewPage /> },
      { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
      { path: '/terms-of-service', element: <TermsOfServicePage /> },
      {
        path: '/email-signature-preview/:profile_hash_id',
        element: <EmailSignaturePreviewPage />,
      },
      {
        path: '/view-profile/:profile_hash_id',
        element: <ViewProfilePage />,
      },
      // protected routes
      {
        element: <AuthenticatedRoutes />,
        children: [
          { path: '/', element: <AuthenticatedRootElement /> },
          {
            path: '/create-group',
            element: <CreateGroupPage />,
          },
          {
            path: '/groups',
            element: <GroupListPage />,
          },
          { path: '/view-profile-sample', element: <SampleProfilePage /> },
          {
            path: '/manage-account/:account_id/:first_name/:last_name/:email',
            element: <AccountSettingsPage />,
          },
          // conditional/superuser routes
          { path: '/integrations', element: <IntegrationsRedirect /> },
          { path: '/settings', element: <AccountSettings /> },
          { path: '/analytics', element: <Analytics /> },
          { path: '/analytics2', element: <Analytics2 /> },
          { path: '/organisations', element: <OrganisationList /> },
          { path: '/activation-keys', element: <ActivationKeysList /> },
          { path: '/profile-generator', element: <ProfileGenerator /> },
          { path: '/pilots', element: <Pilots /> },
          {
            path: '/view-organisation/:organisation_id',
            element: <ViewOrganisation />,
          },
          {
            path: '/edit-profile/:profile_id',
            element: <EditProfile />,
          },
          { path: '/contacts', element: <ContactsList /> },
          { path: '/profiles-list', element: <ProfilesListAdmin /> },
          { path: '/orders-v2/:orderDetailUUID?', element: <OrdersV2 /> },
          // other
          { path: '/edit-shared-profile', element: <EditSharedProfilePage /> },
          { path: '/edit-group/:id', element: <EditGroupPage /> },
          { path: '/products', element: <ProductsListPage /> },
          {
            path: '/templates',
            element: <TemplatesLayout />,
            children: [
              {
                index: true,
                element: <Navigate to="/templates/profile-design" replace />,
              },
              {
                path: 'profile-design',
                element: <ProfileDesign />,
              },
              {
                path: 'contact-exchange',
                element: <ContactExchange />,
              },
              {
                path: 'email-signature',
                element: <EmailSignature />,
              },
              {
                path: 'digital-wallet',
                element: <DigitalWallet />,
              },
            ],
          },
          { path: '/shop', element: <ShopPage /> },
        ],
      },
    ],
  },
  { path: '/test', element: <TestComponents /> },
  { path: '*', element: <NotFoundPage /> },
]);

const isSuperUser = (userScope: string | undefined) => {
  return userScope === 'admin';
};
const isSuperUserAdmin = (
  userScope: string | undefined,
  adminRole: string | null | undefined,
) => {
  return isSuperUser(userScope) && adminRole === null;
};
const isSalesAdmin = (
  userScope: string | undefined,
  adminRole: string | null | undefined,
) => {
  return (
    isSuperUserAdmin(userScope, adminRole) ||
    (userScope === 'admin' && adminRole === 'sale')
  );
};
const isDesignAdmin = (
  userScope: string | undefined,
  adminRole: string | null | undefined,
) => {
  return (
    isSuperUserAdmin(userScope, adminRole) ||
    (userScope === 'admin' && adminRole === 'design')
  );
};
const isOrgAdmin = (
  userScope: string | undefined,
  userRole: string | undefined,
) => {
  return userScope === 'user' && userRole === 'org_admin';
};

function OrdersV2() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? <OrdersV2ListAdminPage /> : <NotFoundPage />;
}

function AuthenticatedRootElement() {
  const { userScope, userRole, orgID } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [creatingOAuthProfile, setCreatingOAuthProfile] = useState(() =>
    OnboardingSession.hasPending(),
  );

  // Hash pending modal display — set by either the normal sign-up path
  // (via router state) or the OAuth path (after claim completes).
  const [pendingModalHash, setPendingModalHash] = useState<string | null>(null);

  // The hash that has passed the 1-profile guard and is ready to display.
  const [modalProfileHash, setModalProfileHash] = useState<string | null>(null);

  // After an OAuth sign-up via onboarding, claim the saved profile.
  useEffect(() => {
    if (!OnboardingSession.hasPending() || !orgID) return;

    const session = OnboardingSession.load<OnboardingSessionData>();
    OnboardingSession.clear();

    if (!session?.profileFormData) {
      setCreatingOAuthProfile(false);
      return;
    }

    const profileFormData = session.profileFormData as WorkingProfileForm;
    const previewToken = session.previewToken ?? null;

    (async () => {
      try {
        // Upload preview assets (best-effort), then claim
        const assets = await uploadPreviewAssets(orgID, profileFormData);
        const claimProfile = buildClaimProfile(profileFormData, assets);

        if (previewToken) {
          const { profile_hash } = await claimOnboarding(
            previewToken,
            claimProfile,
          );
          setPendingModalHash(profile_hash);
        } else {
          // Fallback: no preview token — create profile directly
          const profile_file_links = (profileFormData.profile_file_links ?? [])
            .filter(link => link.status === 'complete')
            .map(({ status: _s, ...link }) => link);

          await profilesAPI.createProfile(orgID, {
            profile: {
              first_name: profileFormData.first_name,
              last_name: profileFormData.last_name,
              middle_name: profileFormData.middle_name,
              pronouns: profileFormData.pronouns,
              job_title: profileFormData.job_title,
              department: profileFormData.department,
              email: profileFormData.email,
              mobile_number: profileFormData.mobile_number,
              note: profileFormData.note,
              photo_file_id: assets.photoFileId,
              company_name: profileFormData.company_name,
              company_phone_number: profileFormData.company_phone_number,
              company_website: profileFormData.company_website,
              city: profileFormData.city,
              state: profileFormData.state,
              post_code: profileFormData.post_code,
              country: profileFormData.country,
              street_address: profileFormData.street_address,
              extra_company_phone_numbers:
                profileFormData.extra_company_phone_numbers,
              contact_exchange_enabled:
                profileFormData.contact_exchange_enabled,
              profile_social_links: profileFormData.profile_social_links,
              profile_quick_links: profileFormData.profile_quick_links,
              profile_file_links,
              additional_information: profileFormData.additional_information,
              settings: assets.logoFileId
                ? {
                    ...profileFormData.settings,
                    logo_file_id: assets.logoFileId,
                  }
                : profileFormData.settings ?? null,
              settings_level: profileFormData.settings_level,
            },
          });
          navigate('/');
        }
      } catch {
        navigate('/');
      } finally {
        setCreatingOAuthProfile(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgID]);

  // Detect hash from normal sign-up path (carried via router navigate state).
  // Clear the state immediately so a page refresh doesn't re-trigger the modal.
  useEffect(() => {
    const hash = (location.state as Record<string, unknown> | null)
      ?.postOnboardingProfileHash;
    if (typeof hash !== 'string' || !hash) return;
    navigate(location.pathname, { replace: true, state: null });
    setPendingModalHash(hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1-profile guard: only show the modal for brand-new onboarding accounts.
  // Once orgID is available and we have a pending hash, fetch the first two
  // profiles. If there is exactly one, the user is new and we show the modal.
  useEffect(() => {
    if (!pendingModalHash || !orgID) return;
    profilesAPI
      .listProfiles({ orgID, pageSize: 2 })
      .then(res => {
        if (res.data.data.length === 1) {
          setModalProfileHash(pendingModalHash);
        }
      })
      .catch(() => {
        // If the check fails, show the modal anyway — better than silently
        // dropping the post-onboarding experience.
        setModalProfileHash(pendingModalHash);
      })
      .finally(() => setPendingModalHash(null));
  }, [pendingModalHash, orgID]);

  if (creatingOAuthProfile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <LoadingAnimation className="w-16 h-16" />
        <p className="text-sm font-medium text-gray-600">
          Setting up your profile…
        </p>
      </div>
    );
  }

  let rootPage;
  if (isSuperUser(userScope)) {
    rootPage = <ProductsListAdminPage />;
  } else if (isOrgAdmin(userScope, userRole)) {
    rootPage = <ProfileListPage />;
  } else {
    rootPage = <ProfileListGroupPage />;
  }

  return (
    <>
      {rootPage}
      {modalProfileHash && (
        <Suspense fallback={null}>
          <PostOnboardingModal
            isOpen={true}
            profileHash={modalProfileHash}
            onClose={() => setModalProfileHash(null)}
          />
        </Suspense>
      )}
    </>
  );
}

function ProfilesListAdmin() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? <ProfileListAdminPage /> : <NotFoundPage />;
}

function AccountSettings() {
  const { adminRole, userRole, userScope } = useAuth();

  if (isSuperUserAdmin(userScope, adminRole)) {
    return <AccountSettingsAdminPage />;
  }

  if (isOrgAdmin(userScope, userRole)) {
    return <AccountSettingsPage />;
  }

  return <NotFoundPage />;
}

function Analytics() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? <AnalyticsAdminPage /> : <OldAnalyticsPage />;
}

function Analytics2() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? (
    <SuperuserAnalyticsPage />
  ) : (
    <AnalyticsPage />
  );
}

function OrganisationList() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? <OrganisationsListPage /> : <NotFoundPage />;
}

function ActivationKeysList() {
  const { adminRole, userScope } = useAuth();

  return isDesignAdmin(userScope, adminRole) ? (
    <ActivationKeysListPage />
  ) : (
    <NotFoundPage />
  );
}

function ProfileGenerator() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? <ProfileGeneratorPage /> : <NotFoundPage />;
}

function Pilots() {
  const { adminRole, userScope } = useAuth();

  return isSalesAdmin(userScope, adminRole) ? (
    <PilotsListPage />
  ) : (
    <NotFoundPage />
  );
}

function ViewOrganisation() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? (
    <ViewOrganisationAdminPage />
  ) : (
    <ViewOrganisationPage />
  );
}

function EditProfile() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? (
    <EditProfileAdminPage />
  ) : (
    <EditProfilePage />
  );
}

function ContactsList() {
  const { userScope } = useAuth();

  return isSuperUser(userScope) ? (
    <ContactListAdminPage />
  ) : (
    <ContactListPage />
  );
}

function TemplatesLayout() {
  const location = useLocation();
  const [, , currentTab] = location.pathname.split('/');

  return (
    <Layout pageName="Templates" className="bg-gray-50">
      <PseudoTabs
        tabs={TemplatesPseudoTabs}
        activeTab={currentTab || 'profile-design'}
      />
      <Outlet />
    </Layout>
  );
}
